import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Sparkle,
  Bank,
  Receipt,
  ShieldStar,
  ArrowRight,
  CheckCircle,
} from "@phosphor-icons/react";

/**
 * First-run tour that fires once per user (see `user.show_onboarding` from /auth/me).
 * Users can Skip or step through 4 spotlight cards. Marking complete calls
 * POST /auth/onboarding/complete and won't show again.
 */
export default function OnboardingTour() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (user && user.show_onboarding && !open) {
      // small delay so the sidebar/frame is mounted first
      const t = setTimeout(() => setOpen(true), 400);
      return () => clearTimeout(t);
    }
  }, [user, open]);

  if (!user || !user.show_onboarding) return null;

  const finish = async (destination = null) => {
    try {
      await api.post("/auth/onboarding/complete");
      if (setUser) {
        setUser({ ...user, show_onboarding: false, onboarding_completed_at: new Date().toISOString() });
      }
    } catch {
      /* non-fatal — the dialog still closes */
    }
    setOpen(false);
    if (destination) navigate(destination);
  };

  const steps = [
    {
      icon: Sparkle,
      eyebrow: "Welcome to LedgerLoom",
      title: `Hi ${user.name?.split(" ")[0] || "there"} — a quick 30-second tour`,
      body: (
        <>
          You&rsquo;re all set. Let&rsquo;s spotlight the two features new
          teammates use most, so day-one feels effortless.
        </>
      ),
      primary: "Show me",
      secondary: "Skip tour",
      primaryAction: () => setStep(1),
      secondaryAction: () => finish(),
    },
    {
      icon: Bank,
      eyebrow: "Feature 1 of 2",
      title: "Bank Holding — post transactions in seconds",
      body: (
        <>
          Import a CSV or PDF bank statement and every unmatched item lands in
          <b> Bank Holding</b>. From there, bulk-post with smart GL suggestions,
          match to invoices, or send an item into
          <b> Intercompany</b> with two clicks.
        </>
      ),
      primary: "Take me there",
      secondary: "Next",
      primaryAction: () => finish("/bank"),
      secondaryAction: () => setStep(2),
    },
    {
      icon: Receipt,
      eyebrow: "Feature 2 of 2",
      title: "Employee Expenses — receipts to reimbursement",
      body: (
        <>
          Employees snap a receipt and LedgerLoom parses the amount, VAT and
          category with AI. Managers approve, and payment posts a reimbursement
          journal automatically — no manual keying.
        </>
      ),
      primary: "Take me there",
      secondary: user.is_superadmin ? "Next" : "Finish",
      primaryAction: () => finish("/expenses"),
      secondaryAction: () => (user.is_superadmin ? setStep(3) : finish()),
    },
    ...(user.is_superadmin
      ? [
          {
            icon: ShieldStar,
            eyebrow: "Master admin",
            title: "You have the Master Admin tab",
            body: (
              <>
                Look at the bottom of the sidebar. From <b>Users &amp; Access</b>{" "}
                you can invite teammates via magic link, assign them to companies,
                and block access instantly across every session.
              </>
            ),
            primary: "Open Master Admin",
            secondary: "Finish",
            primaryAction: () => finish("/admin"),
            secondaryAction: () => finish(),
          },
        ]
      : []),
  ];

  const current = steps[step] || steps[0];
  const Icon = current.icon;
  const total = steps.length;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && finish()}>
      <DialogContent
        className="max-w-lg rounded-lg overflow-hidden p-0 border-0"
        data-testid="onboarding-tour"
      >
        <div
          className="px-6 pt-6 pb-4"
          style={{
            background:
              "linear-gradient(135deg, hsl(111 71% 37%) 0%, hsl(168 40% 24%) 100%)",
            color: "white",
          }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center">
              <Icon size={22} weight="fill" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest opacity-80">
                {current.eyebrow}
              </div>
              <DialogTitle className="text-lg font-semibold mt-0.5">
                {current.title}
              </DialogTitle>
            </div>
          </div>
        </div>

        <div className="px-6 py-5">
          <DialogDescription className="text-sm leading-relaxed text-foreground">
            {current.body}
          </DialogDescription>

          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center gap-1.5" data-testid="onboarding-dots">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-full transition-all"
                  style={{
                    background:
                      i === step
                        ? "hsl(111 71% 37%)"
                        : i < step
                        ? "hsl(111 40% 60%)"
                        : "hsl(0 0% 85%)",
                    width: i === step ? 20 : 8,
                  }}
                />
              ))}
              <div className="ml-3 text-xs text-muted-foreground">
                {step + 1} of {total}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={current.secondaryAction}
                data-testid="onboarding-secondary"
              >
                {current.secondary}
                {step > 0 && step < total - 1 && (
                  <ArrowRight size={14} className="ml-1" />
                )}
                {step === total - 1 && <CheckCircle size={14} className="ml-1" />}
              </Button>
              <Button
                size="sm"
                onClick={current.primaryAction}
                data-testid="onboarding-primary"
              >
                {current.primary}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
